"use client";

import { useEffect, useMemo } from "react";

import { useStudio } from "@/contexts/studio-context";
import type { StudioSurface } from "@/lib/studio/canvas-cover";

/**
 * Déclarer que ce composant cache ces surfaces du canvas du studio.
 *
 * À appeler depuis le calque qui les cache, et non depuis le studio : ce qui
 * rend la déclaration vraie — un fond opaque, un `bottom` qui clampe — est écrit
 * là, et un calque qui deviendrait translucide changerait les deux d'un même
 * geste. Voir `canvas-cover.ts`.
 *
 * Le studio cesse alors de dessiner ce qui est caché, et de présenter tout
 * court si plus rien ne se voit. Il continue en revanche de distribuer le tick,
 * dont vivent les vues DOM posées sur la position du roll.
 *
 * Rien à défaire à la main : le retrait suit le démontage.
 */
export function useStudioCanvasCover(surfaces: readonly StudioSurface[]): void {
  const { studio } = useStudio();

  /*
    Le jeton est l'identité du déclarant, et il doit survivre aux rendus : un
    nouveau jeton par rendu ferait autant de déclarations que de rendus, dont
    une seule serait retirée.
  */
  const token = useMemo(() => ({}), []);

  /*
    La liste est normalisée en clé pour que sa seule identité ne redéclare rien :
    un tableau littéral écrit dans le JSX est neuf à chaque rendu, et le passer
    en dépendance rendrait ce hook aussi bavard que le rendu qui l'appelle.
  */
  const key = [...surfaces].sort().join(",");

  useEffect(() => {
    if (!studio) {
      return;
    }

    return studio.coverCanvas(token, key === "" ? [] : (key.split(",") as StudioSurface[]));
  }, [studio, token, key]);
}
